import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, SyntheticEvent } from 'react'
import { useNewTranslation, useWindowSize } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import NavSidebarContainer, {
    REGISTRATION_VIEW_TYPES,
    SINGLE_TASK_VIEW_TYPES,
    ToggleNavSidebarButton,
} from './NavSidebar'
import { getCampaign, getCampaignSummary } from 'state/campaigns'
import { clearTasksList, getTasksList } from 'state/tasks'
import {
    selectIsMediaModalOpen,
    selectNavBarTitle,
    selectSelectedSubmissionView,
    setMediaModalOpen,
    selectMediaData,
    selectSelectedMediaIndex,
    setSelectedCampaignUuid,
    setSelectedSubmissionView,
    setSelectedTaskUuid,
    resetMultiSelectSetting,
    selectSelectedRegistrationView,
    REGISTRATION_VIEW_ENUM,
    setSelectedRegistrationView,
    selectSidebarStep,
    selectEditRequestHistoryModal,
    selectSelectedStepSummary,
    VERIFY_TAB_TYPE,
    setSelectedStepUuid,
    setSelectedVerifyTab,
    setReviewPendingSubmission,
    setShowSidebar,
    setShowTaskStepInfoInVerify,
    clearSelectedVerifyTab,
    OVERVIEW_ENUM,
    ViewType,
    setSelectedOverview,
    selectSelectedOverview,
    setPartiposterTagFilter,
    setVoteTypeFilter,
    setSubmissionChips,
    setResubmittedTypeFilter,
    selectInfoToastOpen,
    selectInfoToastMessage,
    setInfoToastOpen,
    setInfoToastMessage,
} from 'state/review'
import { theme } from 'theme'
import { selectBatchSubmissionsList, SUBMISSION_VIEW_ENUM, extendFirstStepDeadline } from 'state/submissions'
import ModalWindow from './ModalWindow'
import Loading from 'components/Loading'
import { selectCampaign, selectRegistrationsList, selectRequestsAreLoading, selectSubmissionsList } from 'state'
import { STATE_KEYS } from 'state/requests'
import WarningContainer from './WarningContainer'
import Submissions from './Submissions'
import Registrations from './Registrations'
import MediaViewer from '../../MediaViewer'
import { DeleteTagModal, ProfileTagModal } from 'components/Tag/TagModals'
import { EditRequestHistoryModal } from './Submissions/EditRequestHistory/Modal'
import { selectDeleteTagModalIsOpen, selectProfileTagModalIsOpen } from 'state/partiposter'
import { getForm, saveForm } from 'state/registrations'
import DeadlineHeader from './common/DeadlineHeader'
import { isShowCardReviewComponent } from 'components/utils'
import { traceEvent } from 'config/analytics'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import Button from 'components/Shared/Button/Button'
import { setTeamCountryByCampaign } from 'state/filter'
import Overview from './Overview'
import { SEARCH_PARAMS, SearchParamObject, getSearchParams } from 'components/utils/url'
import { useHistory } from 'react-router-dom'
import Toolbar from './Submissions/Toolbar'
import SubmissionPagination from './Pagination'
import { coreColors } from 'particles/colors'
import { handleEditTagChange, handleRejectTagChange } from 'components/utils/review'
import TagHistoryModal from 'components/Tag/HistoryModal'
import TagAddModal from 'components/Tag/AddModal'
import ExtendDeadlineModal from './common/ExtendDeadlineModal'
import { parseBackendDate } from 'services/dates'
import Report from './Report'
import Toast from 'particles/Toast'

const Review: React.FC<any> = ({ location, className }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const params = qs.parse(location.search) as SearchParamObject
    const entryPoint = params?.entry === 'submission_list' ? 'submission list' : 'direct'
    const history = useHistory()
    const windowSize = useWindowSize()
    const isMobileSize = windowSize <= 768

    const campaignUuid = params.campaign as string
    const taskUuid = params.task as string
    const viewType = params.viewType as ViewType
    const stepUuid = params.step
    const reviewId = params.reviewId
    const verifyTab = params.verifyTab as VERIFY_TAB_TYPE

    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedRegistrationView = useSelector(selectSelectedRegistrationView)
    const selectedOverview = useSelector(selectSelectedOverview)
    const navBarTitle = useSelector(selectNavBarTitle)

    const isDeleteTagModalOpen = useSelector(selectDeleteTagModalIsOpen)
    const isProfileTagModalOpen = useSelector(selectProfileTagModalIsOpen)
    const submissionList = useSelector(selectSubmissionsList)
    const registrationList = useSelector(selectRegistrationsList)
    const batchSubmissionList = useSelector(selectBatchSubmissionsList)
    const selectedStepSummary = useSelector(selectSelectedStepSummary)
    const isInfoToastOpen = useSelector(selectInfoToastOpen)
    const infoToastMessage = useSelector(selectInfoToastMessage)

    const { isOpen: isEditRequestHistoryModalOpen } = useSelector(selectEditRequestHistoryModal)

    const noData =
        selectedRegistrationView !== null
            ? registrationList.length === 0
            : selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
              ? batchSubmissionList.length === 0
              : submissionList.length === 0
    // Please don't include STATE_KEYS.TasksList in isLoading,
    // since DeadlineHeaderAlert uses it to periodically check for step updates.
    const isLoading = useSelector(
        selectRequestsAreLoading([
            STATE_KEYS.SubmissionsList,
            STATE_KEYS.RejectedList,
            STATE_KEYS.DropoutsList,
            STATE_KEYS.Campaign,
            STATE_KEYS.CampaignSummary,
            STATE_KEYS.Profile,
            STATE_KEYS.GetRegistrationForm,
            STATE_KEYS.ExtendFirstStepDeadline,
        ]),
    )
    const campaign = useSelector(selectCampaign(campaignUuid))
    const sidebarStep = useSelector(selectSidebarStep)

    const pendingSubmissionsCount = useMemo(() => {
        return selectedStepSummary?.submission_progress_summary?.pending_count || 0
    }, [selectedStepSummary])

    const submissionViewProperties = useMemo(() => {
        return {
            campaignId: campaignUuid,
            entryPoint: entryPoint,
            pendingSubmissions: pendingSubmissionsCount,
        }
    }, [campaignUuid, entryPoint, pendingSubmissionsCount])

    const resetViewState = useCallback(() => {
        dispatch(setSelectedCampaignUuid(campaignUuid))
        if (taskUuid) dispatch(setSelectedTaskUuid(taskUuid))
        dispatch(clearSelectedVerifyTab())
        dispatch(setShowTaskStepInfoInVerify(false))
        dispatch(setReviewPendingSubmission(null))
        dispatch(setSelectedRegistrationView(null))
        dispatch(setSelectedStepUuid(stepUuid || null))
    }, [campaignUuid, taskUuid, stepUuid])

    useEffect(() => {
        const properties = {
            'campaign id': submissionViewProperties.campaignId,
            'entry point': submissionViewProperties.entryPoint,
            'view type': viewType === 'pending' ? 'single step by status' : 'all submissions',
            'pending submissions': submissionViewProperties.pendingSubmissions,
        }
        traceEvent('view submissions', properties)
    }, [submissionViewProperties.campaignId, viewType])

    useEffect(() => {
        resetViewState()
        const exceptions = [
            SUBMISSION_VIEW_ENUM.DROPOUTS,
            SUBMISSION_VIEW_ENUM.REJECTED,
            REGISTRATION_VIEW_ENUM.DROPOUTS,
        ]

        // handle back navigation from rejected / dropouts
        if (
            !exceptions.includes(viewType as SUBMISSION_VIEW_ENUM | REGISTRATION_VIEW_ENUM) &&
            exceptions.includes(selectedSubmissionView as SUBMISSION_VIEW_ENUM)
        ) {
            dispatch(setSelectedSubmissionView(viewType as SUBMISSION_VIEW_ENUM))
        }

        if (reviewId) {
            dispatch(setShowTaskStepInfoInVerify(true))
            window.scrollTo(0, 0)
            dispatch(setShowSidebar(false))
            dispatch(setReviewPendingSubmission(reviewId))
            dispatch(
                setSelectedVerifyTab({
                    submissionUuid: reviewId,
                    type: verifyTab || VERIFY_TAB_TYPE.REVIEW,
                }),
            )
            dispatch(setSelectedSubmissionView(viewType as SUBMISSION_VIEW_ENUM))
        } else if (viewType === OVERVIEW_ENUM.OVERVIEW) {
            dispatch(setSelectedOverview(OVERVIEW_ENUM.OVERVIEW))
        } else if (viewType === OVERVIEW_ENUM.REPORT) {
            dispatch(setSelectedOverview(OVERVIEW_ENUM.REPORT))
        } else if (
            REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM) ||
            viewType === REGISTRATION_VIEW_ENUM.DROPOUTS
        ) {
            dispatch(setSelectedRegistrationView(viewType as REGISTRATION_VIEW_ENUM))
            if (!isMobileSize) dispatch(setShowSidebar(true))
        } else {
            dispatch(setSelectedSubmissionView(viewType as SUBMISSION_VIEW_ENUM))
            if (!isMobileSize) dispatch(setShowSidebar(true))
        }
    }, [campaignUuid, taskUuid, viewType, stepUuid, reviewId, selectedSubmissionView, verifyTab])

    useEffect(() => {
        if (campaignUuid) {
            dispatch(
                getCampaign({
                    uuid: campaignUuid,
                    onSuccess: campaign => {
                        dispatch(setTeamCountryByCampaign(campaign.uuid))
                    },
                }),
            )
            dispatch(getCampaignSummary({ uuid: campaignUuid }))
            dispatch(getTasksList({ campaignUuid }))
        }
        return () => {
            dispatch(clearTasksList())
            dispatch(setPartiposterTagFilter([]))
            dispatch(setVoteTypeFilter([]))
            dispatch(setResubmittedTypeFilter([]))
            dispatch(
                setSubmissionChips({
                    isPartiposterTagsChipOpen: false,
                    isVoteTypeChipOpen: false,
                    isResubmittedTypeChipOpen: false,
                }),
            )
        }
    }, [campaignUuid])

    useEffect(() => {
        if (campaign && campaign.registration_enable) {
            dispatch(getForm({ uuid: campaignUuid }))
        }
        return () => {
            dispatch(saveForm(null))
        }
    }, [campaign])

    const isMediaModalOpen = useSelector(selectIsMediaModalOpen)
    const mediaData = useSelector(selectMediaData)
    const selectedMediaIndex = useSelector(selectSelectedMediaIndex)
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])

    const selectedRejected =
        selectedRegistrationView === REGISTRATION_VIEW_ENUM.REJECTED ||
        (selectedRegistrationView === null && selectedSubmissionView === SUBMISSION_VIEW_ENUM.REJECTED)
    const selectedDropouts =
        selectedRegistrationView === REGISTRATION_VIEW_ENUM.DROPOUTS ||
        (selectedRegistrationView === null && selectedSubmissionView === SUBMISSION_VIEW_ENUM.DROPOUTS)

    const isOverviewPageMode = selectedOverview === OVERVIEW_ENUM.OVERVIEW
    const isReportPageMode = selectedOverview === OVERVIEW_ENUM.REPORT
    const isRegistrationMode = selectedRegistrationView
        ? REGISTRATION_VIEW_TYPES.includes(selectedRegistrationView)
        : false
    const isSingleTaskSectionMode = selectedRegistrationView
        ? false
        : selectedSubmissionView && SINGLE_TASK_VIEW_TYPES.includes(selectedSubmissionView)
    const isAllSubmissionMode = selectedRegistrationView
        ? false
        : selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS

    const isShowLoading =
        !(
            selectedDropouts ||
            selectedRejected ||
            selectedRegistrationView !== null ||
            selectedOverview ||
            (sidebarStep && !isShowCardReviewComponent(sidebarStep.type))
        ) && isLoading

    const showDeadlineHeader =
        (isRegistrationMode || isSingleTaskSectionMode || isAllSubmissionMode || isOverviewPageMode) &&
        !isLoading &&
        campaign

    const showOverviewPage = isOverviewPageMode && !isLoading && campaign

    const rootRef = useRef<HTMLDivElement>(null)
    const navRef = useRef<HTMLDivElement>(null)
    const deadlineHeaderRef = useRef<HTMLDivElement>(null)
    const warningContainerRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const tableHeaderBarRef = useRef<HTMLDivElement | null>(null)
    const filterSortBarRef = useRef<HTMLDivElement | null>(null)

    const [isWarningEmpty, setisWarningEmpty] = useState<boolean>(false)

    const [deadlineHeaderElHeight, setDeadlineHeaderElHeight] = useState<number>(0)
    const [warningElHeight, setWarningElHeight] = useState<number>(0)
    const [headerElHeight, setHeaderElHeight] = useState<number>(0)
    const [tableHeaderBarHeight, setTableHeaderBarHeight] = useState<number>(0)
    const [filterSortBarHeight, setFilterSortBarHeight] = useState<number>(0)
    const [isExtendDeadlineModalOpen, setIsExtendDeadlineModalOpen] = useState<boolean>(false)

    useLayoutEffect(() => {
        const navBarObserver = new ResizeObserver(entries => {
            entries.forEach(entry => {
                if (rootRef.current) {
                    rootRef.current.style.setProperty('--nav-bar-height', entry.target.clientHeight + 'px')
                }
            })
        })
        if (navRef.current) {
            navBarObserver.observe(navRef.current)
        }
        return () => navBarObserver.disconnect()
    }, [])

    useLayoutEffect(() => {
        const updateRefs = () => {
            tableHeaderBarRef.current = rootRef.current && rootRef.current?.querySelector('.table-header-bar')
            filterSortBarRef.current = rootRef.current && rootRef.current?.querySelector('.filter-and-sort-bar')

            setWarningElHeight(warningContainerRef.current?.clientHeight || 0)
            setHeaderElHeight(headerRef.current?.clientHeight || 0)
            setDeadlineHeaderElHeight(deadlineHeaderRef.current?.clientHeight || 0)
            setTableHeaderBarHeight(tableHeaderBarRef.current?.clientHeight || 0)
            setFilterSortBarHeight(filterSortBarRef.current?.clientHeight || 0)

            setisWarningEmpty((warningContainerRef?.current as HTMLDivElement | null)?.innerHTML?.trim() === '')
        }

        updateRefs() // Initial update

        // Callback when DOM mutations occur
        const observer = new MutationObserver(updateRefs)

        // Start observing the root node for mutations
        if (rootRef.current) {
            observer.observe(rootRef.current, { childList: true, subtree: true })
        }

        // Cleanup function to disconnect the observer when component unmounts
        return () => {
            observer.disconnect()
        }
    }, [])

    const contentHeight =
        warningElHeight +
        deadlineHeaderElHeight +
        headerElHeight +
        filterSortBarHeight +
        tableHeaderBarHeight +
        124 - // 124 is the sum of the navbar height + all gaps and margins
        (warningElHeight ? 0 : 16) // Subtracting 16px accounts for the 16px gap when the warning container is displayed

    const handleExtendDeadline = () => {
        sidebarStep?.uuid &&
            dispatch(
                extendFirstStepDeadline({
                    stepUuid: sidebarStep.uuid,
                    stepName: sidebarStep?.data.title,
                    newStep1EndDate: parseBackendDate(sidebarStep?.extended_deadline, campaign.country, {
                        addSeconds: -1,
                    }),
                    onSuccessCallback: () => {
                        setIsExtendDeadlineModalOpen(false)
                        dispatch(getTasksList({ campaignUuid }))
                    },
                }),
            )
    }

    const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return
        }

        dispatch(setInfoToastOpen(false))
        dispatch(setInfoToastMessage(''))
    }

    return (
        <div ref={rootRef} className={className}>
            <div className="Nav-bar" ref={navRef}>
                <Text scale="bodyS400" color="primaryInverse">
                    {navBarTitle}
                </Text>
                <div className="button-groups">
                    <Button
                        type="button"
                        buttonType="text"
                        size="s"
                        customStyle={{
                            color: theme.newColors.neutral50,
                            backgroundColor: 'transparent',
                            ':hover': { backgroundColor: 'transparent' },
                        }}
                        text={newT('button.rejected', 'Rejected').toUpperCase()}
                        onClick={() => {
                            dispatch(resetMultiSelectSetting())
                            // prevent same page redirection
                            if (viewType !== SUBMISSION_VIEW_ENUM.REJECTED) {
                                const searchParams = getSearchParams({
                                    add: [{ key: SEARCH_PARAMS.viewType, value: SUBMISSION_VIEW_ENUM.REJECTED }],
                                    remove: [SEARCH_PARAMS.reviewId],
                                })
                                history.push(searchParams)
                            }
                        }}
                    />

                    <Button
                        type="button"
                        buttonType="text"
                        size="s"
                        customStyle={{
                            color: theme.newColors.neutral50,
                            backgroundColor: 'transparent',
                            ':hover': { backgroundColor: 'transparent' },
                        }}
                        text={newT('button.dropouts', 'Dropouts').toUpperCase()}
                        onClick={(): void => {
                            dispatch(resetMultiSelectSetting())
                            // prevent same page redirection
                            if (
                                ![REGISTRATION_VIEW_ENUM.DROPOUTS, SUBMISSION_VIEW_ENUM.DROPOUTS].includes(
                                    viewType as SUBMISSION_VIEW_ENUM | REGISTRATION_VIEW_ENUM,
                                )
                            ) {
                                if (REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM)) {
                                    const registrationSearchParams = getSearchParams({
                                        add: [{ key: SEARCH_PARAMS.viewType, value: REGISTRATION_VIEW_ENUM.DROPOUTS }],
                                        remove: [SEARCH_PARAMS.reviewId],
                                    })
                                    history.push(registrationSearchParams)
                                } else {
                                    const searchParams = getSearchParams({
                                        add: [{ key: SEARCH_PARAMS.viewType, value: SUBMISSION_VIEW_ENUM.DROPOUTS }],
                                        remove: [SEARCH_PARAMS.reviewId],
                                    })
                                    history.push(searchParams)
                                }
                            }
                        }}
                    />
                </div>
            </div>
            {/* EditRequestHistoryModal must need to place earlier than MediaViewer to avoid z-index fighting issue */}
            {isEditRequestHistoryModalOpen && <EditRequestHistoryModal />}
            <div className="review-container">
                <NavSidebarContainer />
                <div className="review-submissions-container">
                    {!noData && !isOverviewPageMode ? (
                        <WarningContainer
                            ref={warningContainerRef}
                            campaignUuid={campaignUuid}
                            className={isWarningEmpty ? 'no-display' : 'warning-container'}
                        />
                    ) : null}
                    {/* hide if report page mode and not mobile size */}
                    {(viewType !== OVERVIEW_ENUM.REPORT || isMobileSize) && (
                        <div className="review-submissions-header">
                            <ToggleNavSidebarButton />
                            {showDeadlineHeader ? (
                                <DeadlineHeader
                                    ref={deadlineHeaderRef}
                                    campaignUuid={campaignUuid}
                                    isRegistrationMode={isRegistrationMode}
                                    isSingleTaskSectionMode={isSingleTaskSectionMode ? isSingleTaskSectionMode : false}
                                    isOverviewPageMode={isOverviewPageMode}
                                    isAllSubmissionMode={isAllSubmissionMode}
                                    onExtendDeadline={() => setIsExtendDeadlineModalOpen(true)}
                                    isDeadlineExtendable={sidebarStep?.is_deadline_extendable ?? false}
                                />
                            ) : null}
                        </div>
                    )}

                    <div className="review-submissions-wrapper">
                        <div className="review-submissions">
                            {isReportPageMode ? (
                                <Report campaign={campaign} />
                            ) : showOverviewPage ? (
                                <Overview campaignUuid={campaignUuid} />
                            ) : (
                                <>
                                    <Toolbar
                                        handleEditTagChange={handleEditTagChange}
                                        handleRejectTagChange={handleRejectTagChange}
                                    />

                                    <ModalWindow handleRejectTagChange={handleRejectTagChange} />
                                    {/* todo: need to re-organize it after the tables are all updated to new table */}
                                    {isShowLoading && <Loading className="submission-loader" />}
                                    {selectedRegistrationView ? (
                                        <div className="review-table">
                                            <Registrations tableHeight={`calc(100vh - ${contentHeight}px)`} />
                                        </div>
                                    ) : (
                                        <Submissions
                                            tableHeight={`calc(100vh - ${contentHeight}px)`}
                                            handleEditTagChange={handleEditTagChange}
                                            handleRejectTagChange={handleRejectTagChange}
                                        />
                                    )}

                                    <SubmissionPagination />
                                    {mediaData && isMediaModalOpen && (
                                        <MediaViewer
                                            mediaData={mediaData}
                                            maxHeight="200px"
                                            showMediaIndex={selectedMediaIndex}
                                            isOpenModal={isMediaModalOpen}
                                            setModalOpen={setMediaModal}
                                            popupOnly={true}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <Toast message={infoToastMessage} open={isInfoToastOpen} onClose={handleClose} />
                </div>
            </div>
            <TagHistoryModal />
            <TagAddModal />
            {isProfileTagModalOpen && <ProfileTagModal />}
            {isDeleteTagModalOpen && <DeleteTagModal />}
            {isExtendDeadlineModalOpen && (
                <ExtendDeadlineModal
                    stepName={sidebarStep?.data.title}
                    newStep1EndDate={
                        sidebarStep
                            ? parseBackendDate(sidebarStep?.extended_deadline, campaign.country, { addSeconds: -1 })
                            : ''
                    }
                    onExtendDeadline={handleExtendDeadline}
                    onClose={() => setIsExtendDeadlineModalOpen(false)}
                />
            )}
        </div>
    )
}

export default styled(Review)`
    width: 100%;
    height: 100%;
    background: ${theme.newColors.neutral100};
    flex-direction: column;
    .Nav-bar {
        gap: 12px;
        display: flex;
        height: 52px;
        align-items: center;
        justify-content: space-between;
        background-color: ${theme.newColors.primary500};
        box-sizing: border-box;
        padding: 24px;
        flex-direction: row;
        @media screen and (max-width: 768px) {
            flex-direction: column;
            height: auto;
            align-items: flex-start;
            padding: 12px 16px;
        }
        .button-groups {
            display: flex;
        }
    }
    .review-container {
        width: 100%;
        min-height: calc(100% - 52px);
        padding: 0 24px;
        @media screen and (max-width: 768px) {
            min-height: calc(100% - var(--nav-bar-height, 84px));
            padding: 0 16px;
        }
        max-height: calc(100vh - 52px);
        display: flex;
        box-sizing: border-box;
        gap: 16px;
    }
    .review-submissions-container {
        display: flex;
        gap: 16px;

        flex-direction: column;
        width: 100%;
        max-height: 100%;
        overflow: auto;
        padding-top: ${theme.newSpacing.s};

        .review-submissions-header {
            display: flex;
            flex-direction: row;
            gap: 12px;
        }

        .review-submissions-wrapper {
            background: ${coreColors.white50};
            max-width: 100%;
            height: 100%;
            overflow: auto;
        }
    }
    .review-submissions {
        @media screen and (max-width: 768px) {
            min-width: 1060px;
        }
        display: flex;
        flex-direction: column;
        height: 100%;

        .submission-loader {
            height: 100%;
        }
    }
    .no-display {
        display: none;
    }
`
